import { useTranslation } from 'react-i18next';
import { CheckSquare, Square } from 'lucide-react';
import type { PerioExamListItem } from '@/hooks/usePerioExams';

// ── Props ─────────────────────────────────────────────────────────────────────

interface PerioTimelineProps {
  exams: PerioExamListItem[];
  activeExamId: string | null;
  compareExamIds: string[];
  onSelectExam: (examId: string) => void;
  onToggleCompare: (examId: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PerioTimeline({
  exams,
  activeExamId,
  compareExamIds,
  onSelectExam,
  onToggleCompare,
}: PerioTimelineProps) {
  const { t } = useTranslation();

  if (exams.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {t('dental.perio.timeline.title')}
        </p>
        <p className="mt-3 text-sm text-gray-400 dark:text-gray-500">
          {t('dental.perio.newExam', 'Ingen undersøgelser endnu.')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {t('dental.perio.timeline.title')}
        </p>
      </div>

      <ul className="divide-y divide-gray-100 dark:divide-gray-800">
        {exams.map((exam, idx) => {
          const isActive = exam.exam_id === activeExamId;
          const isCompareSelected = compareExamIds.includes(exam.exam_id);
          const date = new Date(exam.recorded_at).toLocaleDateString('da-DK', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          });

          return (
            <li key={exam.exam_id} className={`${isActive ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`}>
              <div className="flex items-center gap-2 px-4 py-3">
                {/* Compare toggle */}
                <button
                  type="button"
                  title={t('dental.perio.timeline.compareSelect')}
                  onClick={() => onToggleCompare(exam.exam_id)}
                  className="shrink-0 text-gray-400 hover:text-indigo-600 dark:text-gray-500 dark:hover:text-indigo-400"
                >
                  {isCompareSelected ? (
                    <CheckSquare className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                </button>

                {/* Exam details button */}
                <button
                  type="button"
                  onClick={() => onSelectExam(exam.exam_id)}
                  className="flex min-w-0 flex-1 flex-col items-start text-left"
                >
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                      {t('dental.perio.examLabel', { n: exams.length - idx, defaultValue: `Undersøgelse #${exams.length - idx}` })}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{date}</span>
                  </div>

                  <div className="mt-0.5 flex items-center gap-2">
                    {exam.recorded_by_name && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {exam.recorded_by_name}
                      </span>
                    )}
                    {exam.worst_pd_mm !== null && (
                      <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {t('dental.perio.fields.pd', 'PD')} max {exam.worst_pd_mm} mm
                      </span>
                    )}
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {exam.measurement_count} mål.
                    </span>
                  </div>
                </button>

                {isActive && (
                  <span className="shrink-0 rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                    Aktiv
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
